<template>
  <div>
    <cbs-card-actions
      action-close
      title="Upload File"
      @close="onCancel"
    >
      <b-card-body>
        <b-form-file
          v-model="file"
          placeholder="Choose a file or drop it here..."
          drop-placeholder="Drop file here..."
        />
        <!-- buttons -->
        <b-button-toolbar
          key-nav
          aria-label="Toolbar with button groups"
          class="demo-inline-spacing"
        >
          <b-button-group
            size="sm"
            class="cbs-inline-spacing"
          >
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="danger"
              @click="onCancel"
            >
              <feather-icon icon="XIcon" />
              Cancel
            </b-button>
          </b-button-group>
          <b-button-group
            size="sm"
            class="cbs-inline-spacing"
          >
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="warning"
              @click="clearFile"
            >
              <feather-icon icon="DeleteIcon" />
              Clear
            </b-button>
          </b-button-group>
          <b-button-group
            size="sm"
            class="cbs-inline-spacing"
          >
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="success"
              :disabled="!file || uploading"
              @click="onUpload"
            >
              <feather-icon icon="UploadIcon" />
              {{ uploading ? 'Uploading…' : 'Upload' }}
            </b-button>
          </b-button-group>
          <b-button-group
            v-if="isAdmin()"
            size="sm"
            class="cbs-inline-spacing"
          >
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              :variant="isDebug ? 'primary' : 'outline-primary'"
              @click="isDebug = !isDebug"
            >
              <feather-icon icon="CodeIcon" /> Debug
            </b-button>
          </b-button-group>
        </b-button-toolbar>
        <!-- /buttons -->

        <!-- debug -->
        <b-row v-if="isDebug">
          <b-col>
            <div style="margin-top:10px;">
              <div style="margin:10px">
                Props: <json-viewer
                  :value="getProps()"
                  :expand-depth="0"
                  sort
                />
                Data: <json-viewer
                  :value="getData()"
                  :expand-depth="0"
                  sort
                />
                Session: <json-viewer
                  :value="session()"
                  :expand-depth="0"
                  sort
                />
              </div>
            </div>
          </b-col>
        </b-row>
        <!-- /debug -->
      </b-card-body>
    </cbs-card-actions>
  </div>
</template>

<script>
import useCubus from '@/cubus/services/useCubus'
import {
  BButton, BButtonGroup, BButtonToolbar, BCardBody, BFormFile,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import CbsCardActions from '@/cubus/components/cbs-card-actions/CbsCardActions.vue'
import useJwt from '@/cubus/jwt/useJwt'

export default {
  name: 'CbsFileUpload',
  components: {
    BFormFile,
    BButtonToolbar,
    BButtonGroup,
    BButton,
    CbsCardActions,
    BCardBody,
  },
  emits: [
    'save',
  ],
  directives: {
    Ripple,
  },
  data() {
    return {
      file: null,
      uploading: false,
      isDebug: false,
      record: null,
    }
  },
  methods: {
    onCancel() {},
    clearFile() {
      this.file = null
    },
    // BUG-529: загрузка — штатной ручкой `/api/upload` методом `uploadfile`, как у остальных форм: антивирус,
    // шифрование и S3 там уже есть. Прежний путь (`/api/s3/presign-upload` + `uploadfiles3`) звал ручку и метод,
    // которых в бэкенде нет и не было, — добавить файл из карточки таблицы файлов было нельзя (404/403).
    // Прямая загрузка в S3 шла бы мимо антивируса и шифрования (BUG-119, PROP-154).
    async onUpload() {
      if (!this.file || this.uploading) return
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('query', JSON.stringify({ method: 'uploadfile', param: { filekeys: ['file0'] } }))
        formData.append('file0', this.file)
        const response = await useJwt.upload(formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        if (response.data && response.data.error) {
          useCubus.toastError(this, response.data.error)
          return
        }
        const thread = response.data && response.data.thread
        if (!thread) {
          useCubus.toastError(this, 'No thread name provided.')
          return
        }
        const result = await this.waitThread(thread)
        if (result.error) {
          useCubus.toastError(this, result.error)
        } else if (result.record) {
          this.onSave(result.record)
          this.file = null
        } else {
          useCubus.toastError(this, 'Upload finished without a record.')
        }
      } catch (error) {
        console.log('upload error', error)
        useCubus.toastError(this, error)
      } finally {
        this.uploading = false
      }
    },
    // Загрузка идёт в фоне (скан антивирусом крупного файла — до минут): опрос треда до `done`/`error`.
    async waitThread(threadname) {
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, 1000))
        // eslint-disable-next-line no-await-in-loop
        const response = await useJwt.query({ query: { method: 'thread', param: { threadname } } })
        const thr = response.data && response.data.thread
        if (response.data && response.data.error) return { error: response.data.error }
        if (thr && thr.status === 'error') return { error: thr.error || 'Upload failed.' }
        if (thr && thr.status === 'done') return { record: thr.result && thr.result.record, error: thr.error }
      }
    },
    isAdmin() {
      return useCubus.isAdmin()
    },
    onSave(rec) {
      this.$emit('save', rec)
    },
  },
}
</script>

<style scoped>

</style>
